import { Fragment } from 'react';

import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';

const ScheduleDialog = ({ isOpen, title, description, onClose, isBusy, footer, children }) => (
    <Transition.Root show={isOpen} as={Fragment} appear>
        <Dialog as="div" className="relative z-[100020]" onClose={() => !isBusy && onClose()}>

            <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="fixed inset-0 bg-gray-900/50" aria-hidden="true" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <Dialog.Panel className="w-full max-w-[460px] rounded-xl bg-white shadow-xl">

                            <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
                                <div>
                                    <Dialog.Title className="text-[16px] font-semibold text-gray-900">{title}</Dialog.Title>
                                    {description && <p className="mt-1 text-[13px] leading-snug text-gray-500">{description}</p>}
                                </div>
                                <button
                                    type="button"
                                    aria-label="Fechar"
                                    disabled={isBusy}
                                    onClick={onClose}
                                    className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                                >
                                    <X className="h-[18px] w-[18px]" />
                                </button>
                            </div>

                            <div className="px-6 py-5">
                                {children}
                            </div>

                            {footer && (
                                <div className="flex justify-end gap-2 border-t border-gray-200 px-6 py-4">
                                    {footer}
                                </div>
                            )}

                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </div>

        </Dialog>
    </Transition.Root>
);

export const ScheduleOptionGroup = ({ name, label, options, value, onChange, disabled }) => (
    <fieldset className="mb-5">
        <legend className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-400">{label}</legend>
        <div className="flex flex-col gap-2">
            {options.map(option => (
                <label
                    key={option.key}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3.5 py-3 ${
                        value === option.key ? 'border-primary bg-primaryTint' : 'border-gray-200 hover:bg-gray-50'
                    } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                    <input
                        type="radio"
                        name={name}
                        value={option.key}
                        checked={value === option.key}
                        disabled={disabled}
                        onChange={() => onChange(option.key)}
                        className="mt-0.5 text-primary focus:ring-primary"
                    />
                    <span>
                        <span className="block text-[13.5px] font-semibold text-gray-900">{option.label}</span>
                        <span className="block text-[12.5px] text-gray-500">{option.description}</span>
                    </span>
                </label>
            ))}
        </div>
    </fieldset>
);

export const ScheduleDateTimeFields = ({ idPrefix, dateLabel, timeLabel, date, time, minDate, maxDate, onDateChange, onTimeChange, disabled }) => (
    <div className="mb-2 grid grid-cols-2 gap-3">
        <div>
            <label htmlFor={`${idPrefix}-date`} className="mb-1.5 block text-[12.5px] font-medium text-gray-700">{dateLabel}</label>
            <input
                id={`${idPrefix}-date`}
                type="date"
                value={date}
                min={minDate || undefined}
                max={maxDate || undefined}
                disabled={disabled}
                onChange={(event) => onDateChange(event.target.value)}
                className="form-input w-full rounded-md border-gray-300 text-[13.5px] focus:border-primary focus:ring-primary"
            />
        </div>
        <div>
            <label htmlFor={`${idPrefix}-time`} className="mb-1.5 block text-[12.5px] font-medium text-gray-700">{timeLabel}</label>
            <input
                id={`${idPrefix}-time`}
                type="time"
                value={time}
                disabled={disabled}
                onChange={(event) => onTimeChange(event.target.value)}
                className="form-input w-full rounded-md border-gray-300 text-[13.5px] focus:border-primary focus:ring-primary"
            />
        </div>
    </div>
);

export const ScheduleErrorMessage = ({ message }) => message
    ? <p role="alert" className="mt-3 rounded-md bg-red-50 px-3 py-2 text-[13px] text-red-700">{message}</p>
    : null;

export const scheduleDialogButtonClassNames = {
    secondary: 'rounded-lg border border-gray-300 px-4 py-2 text-[13.5px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50',
    primary: 'rounded-lg bg-primary px-4 py-2 text-[13.5px] font-semibold text-white hover:bg-primaryDark disabled:cursor-not-allowed disabled:opacity-50',
};

export default ScheduleDialog;
